import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Download, QrCode, WifiOff } from "lucide-react";
import api from "../lib/api";

/**
 * Sekcja pobierania aplikacji mobilnej (.apk dla Androida).
 * - iOS: nie wyświetlane wcale.
 * - Telefony (poza iOS): tylko przycisk pobierania.
 * - Desktop: przycisk + kod QR.
 * Sekcja chowa się również jeśli backend nie udostępnia żadnego pliku .apk.
 */
function detectPlatform() {
  if (typeof navigator === "undefined") return { ios: false, mobile: false };
  const ua = navigator.userAgent || "";
  // iOS: iPhone/iPad/iPod + nowe iPady udające MacIntel z touch screenem
  const ios =
    /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document);
  const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
  return { ios, mobile };
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
}

export default function AppDownloadSection() {
  const [info, setInfo] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const platform = useMemo(detectPlatform, []);

  useEffect(() => {
    api
      .get("/app/info")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ available: false }))
      .finally(() => setLoaded(true));
  }, []);

  // Nic nie pokazujemy zanim sprawdzimy backend ani gdy plik nie istnieje, ani na iOS.
  if (!loaded || !info?.available || platform.ios) return null;

  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/app/download`;
  const qrUrl = `${process.env.REACT_APP_BACKEND_URL}/api/app/go?src=qr`;
  const showQr = !platform.mobile;

  // Fire-and-forget tracker + let the <a> navigation continue to the actual download.
  const handleDownloadClick = () => {
    try {
      const body = new Blob([JSON.stringify({})], { type: "application/json" });
      const trackerUrl = `${process.env.REACT_APP_BACKEND_URL}/api/app/track-download?source=button`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(trackerUrl, body);
      } else {
        fetch(trackerUrl, { method: "POST", keepalive: true }).catch(() => {});
      }
    } catch {
      /* nigdy nie blokuj pobierania z powodu trackera */
    }
  };

  return (
    <section className="px-4 sm:px-6 md:px-10 pt-1 pb-3" data-testid="app-download-section">
      <div
        className="rounded-[16px] overflow-hidden flex flex-col md:flex-row items-stretch"
        style={{
          background: "linear-gradient(135deg, #0049A8 0%, #003580 100%)",
          border: "1px solid #003580",
          boxShadow: "0 4px 14px rgba(0,73,168,0.18)",
        }}
      >
        {/* Lewa kolumna: tytuł + przycisk */}
        <div className="flex-1 px-6 sm:px-8 py-6 flex flex-col gap-4 justify-center">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center justify-center"
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                backgroundColor: "rgba(255,153,0,0.18)",
                border: "1px solid rgba(255,153,0,0.4)",
              }}
            >
              <Smartphone size={22} color="#FF9900" />
            </div>
            <div>
              <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 900, lineHeight: 1.2, letterSpacing: 0.3 }}>
                Aplikacja mobilna festiwalu
              </h2>
              <p style={{ color: "rgba(255,255,255,0.75)", fontSize: 13, marginTop: 2 }}>
                Cały informator w Twojej kieszeni — Android
              </p>
            </div>
          </div>

          <div
            className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full"
            style={{
              backgroundColor: "rgba(76,175,80,0.15)",
              border: "1px solid rgba(76,175,80,0.45)",
            }}
            data-testid="app-offline-badge"
          >
            <WifiOff size={14} color="#8BE28E" />
            <span style={{ color: "#C8F5C8", fontSize: 12, fontWeight: 700, letterSpacing: 0.3 }}>
              Po instalacji działa też offline
            </span>
          </div>

          <a
            href={downloadUrl}
            onClick={handleDownloadClick}
            className="inline-flex items-center justify-center gap-2 sm:gap-3 px-5 py-3 sm:px-7 sm:py-[14px] rounded-[12px] transition-transform hover:scale-[1.02] self-start"
            style={{
              backgroundColor: "#FF9900",
              boxShadow: "0 4px 12px rgba(212,85,0,0.35)",
            }}
            data-testid="app-download-btn"
          >
            <Download size={20} color="#fff" />
            <span className="text-base sm:text-lg" style={{ color: "#fff", fontWeight: 800, letterSpacing: 0.4 }}>
              Pobierz aplikację
            </span>
          </a>

          <div className="flex items-center gap-3 flex-wrap" style={{ color: "rgba(255,255,255,0.65)", fontSize: 12 }}>
            {info.sizeBytes ? <span>📦 {formatBytes(info.sizeBytes)}</span> : null}
            <span>Android · plik .apk</span>
          </div>

          <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 12, lineHeight: 1.55 }}>
            Po pobraniu otwórz plik na telefonie i potwierdź instalację. Zainstalowana
            aplikacja działa bez internetu — cały program festiwalu masz zawsze pod
            ręką. Wymaga systemu Android (iPhone nie jest obsługiwany).
          </p>
        </div>

        {/* Prawa kolumna: QR – tylko na desktopie */}
        {showQr && (
          <div
            className="flex flex-col items-center justify-center gap-2 px-6 py-6 md:px-7 md:py-6"
            style={{
              backgroundColor: "rgba(255,255,255,0.07)",
              borderLeft: "1px solid rgba(255,255,255,0.12)",
            }}
            data-testid="app-download-qr"
          >
            <div className="flex items-center gap-2" style={{ color: "rgba(255,255,255,0.85)", fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
              <QrCode size={14} /> Zeskanuj telefonem
            </div>
            <div className="bg-white p-3 rounded-[12px]" style={{ boxShadow: "0 2px 8px rgba(0,0,0,0.15)" }}>
              <QRCodeSVG
                value={qrUrl}
                size={148}
                level="M"
                includeMargin={false}
                data-testid="app-qr-code"
              />
            </div>
            <span style={{ color: "rgba(255,255,255,0.55)", fontSize: 11 }}>
              Pobieranie startuje po zeskanowaniu
            </span>
          </div>
        )}
      </div>
    </section>
  );
}
